import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API,
    timeout: 180000,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
});

export async function geocode(q) {
    const r = await api.get("/geocode", { params: { q } });
    return r.data.results || [];
}

export async function generateRoadmap(payload) {
    const r = await api.post("/roadmap/generate", payload);
    return r.data;
}

export async function getChart(hash) {
    const r = await api.get(`/chart/${hash}`);
    return r.data;
}

// -- Chart Journal · Compounding Memory ------------------------------------
// Returns the tier-gated journal + persona for a chart the caller owns.
// Response shape: { entries[], persona, tier, cap, total, upsell }.
export async function getChartJournal(chartHash) {
    const r = await api.get(`/chart/${chartHash}/journal`);
    return r.data;
}

// Manually refresh the persona digest — usually called after the user
// has just given a batch of feedback and wants next answer to reflect it.
export async function rollupChartJournal(chartHash) {
    const r = await api.post(`/chart/${chartHash}/journal/rollup`);
    return r.data;
}

export function pdfUrl(hash, lang) {
    // Language defaults to the active i18n choice so a Hindi user gets a
    // Devanagari-rendered PDF without any extra UI plumbing. Callers can
    // override with an explicit code (e.g. for an export-language picker).
    const code = lang || (typeof window !== "undefined" ? (window.localStorage.getItem("lodestar.lang") || "en") : "en");
    return `${API}/chart/${hash}/pdf?lang=${encodeURIComponent(code)}`;
}

export function docxUrl(hash) {
    return `${API}/chart/${hash}/docx`;
}

// ---- saved conversations (Phase P) ----
export async function saveConversation(chartHash, messages, title) {
    const r = await api.post(`/chart/${chartHash}/conversations`, { messages, title });
    return r.data;
}
export async function listConversations(chartHash) {
    const r = await api.get(`/chart/${chartHash}/conversations`);
    return r.data;
}
export async function getConversation(chartHash, convId) {
    const r = await api.get(`/chart/${chartHash}/conversations/${convId}`);
    return r.data;
}
export async function deleteConversation(chartHash, convId) {
    const r = await api.delete(`/chart/${chartHash}/conversations/${convId}`);
    return r.data;
}

// ---- subscriptions / payments ----
export async function getCatalogue() {
    const r = await api.get("/payments/catalogue");
    return r.data.packages || [];
}

export async function getMySubscription() {
    const r = await api.get("/account/subscription");
    return r.data;
}

export async function createCheckout(package_id) {
    const r = await api.post("/payments/checkout", {
        package_id,
        origin_url: window.location.origin,
    });
    return r.data;
}

export async function pollCheckoutStatus(session_id) {
    const r = await api.get(`/payments/checkout/status/${session_id}`);
    return r.data;
}

// ---- admin ----
export async function adminOverview() {
    const r = await api.get("/admin/overview");
    return r.data;
}
export async function adminUsers(params = {}) {
    const r = await api.get("/admin/users", { params });
    return r.data;
}
export async function adminSubscriptions(params = {}) {
    const r = await api.get("/admin/subscriptions", { params });
    return r.data;
}
export async function adminTransactions(params = {}) {
    const r = await api.get("/admin/transactions", { params });
    return r.data;
}
export async function adminReconcilePayments() {
    const r = await api.post("/admin/payments/reconcile");
    return r.data;
}
export async function adminGrant(user_id, tier, days = 30) {
    const r = await api.post("/admin/grant", { user_id, tier, days });
    return r.data;
}
export async function adminLlmCalls(params = {}) {
    const r = await api.get("/admin/llm-calls", { params });
    return r.data;
}
export async function adminChartLatency(params = {}) {
    const r = await api.get("/admin/chart-latency", { params });
    return r.data;
}
export async function adminChatInsights(days = 30) {
    const r = await api.get("/admin/chat-insights", { params: { days } });
    return r.data;
}
export async function adminPromoConfigGet() {
    const r = await api.get("/admin/promo-config");
    return r.data;
}
export async function adminPromoConfigPut(patch) {
    const r = await api.put("/admin/promo-config", patch);
    return r.data;
}
export async function adminLlmHealth() {
    const r = await api.get("/admin/llm-health");
    return r.data;
}

// ---- Phase D+ Learning loop ----
export async function postWindowFeedback(payload) {
    const r = await api.post("/feedback/window", payload);
    return r.data;
}
export async function getMyAccuracy() {
    const r = await api.get("/feedback/accuracy");
    return r.data;
}
export async function getPatternExamples({ chart_hash, domain, window_start, tone }) {
    const r = await api.get("/feedback/pattern-examples", {
        params: { chart_hash, domain, window_start, tone },
    });
    return r.data;
}

// ---- auth ----
export async function authMe() {
    const r = await api.get("/auth/me");
    return r.data.user;
}

export async function authRegister(body) {
    const r = await api.post("/auth/register", body);
    return r.data.user;
}

export async function authLogin(body) {
    const r = await api.post("/auth/login", body);
    return r.data.user;
}

export async function authLogout() {
    await api.post("/auth/logout");
}

export async function authGoogleSession(session_id) {
    const r = await api.post("/auth/google/session", { session_id });
    return r.data.user;
}

// ---- account ----
export async function listMyCharts() {
    const r = await api.get("/account/charts");
    return r.data.charts || [];
}

export async function deleteMyChart(hash) {
    await api.delete(`/account/charts/${hash}`);
}

export async function deleteMyAccount() {
    await api.delete("/account/me");
}

// ---- password reset & change ----
export async function forgotPassword(email) {
    const r = await api.post("/auth/forgot-password", { email });
    return r.data;
}

export async function resetPassword(token, password) {
    const r = await api.post("/auth/reset-password", { token, password });
    return r.data.user;
}

export async function changePassword(current_password, new_password) {
    await api.post("/auth/change-password", { current_password, new_password });
}

// ---- profile ----
export async function getProfile() {
    const r = await api.get("/account/profile");
    return r.data.profile;
}

export async function saveProfile(patch) {
    const r = await api.put("/account/profile", patch);
    return r.data.profile;
}

// ---- email preferences (Habit Loop — Weekly Cosmic Forecast) ----
export async function getEmailPrefs() {
    const r = await api.get("/account/email-prefs");
    return r.data.email_prefs;
}

export async function saveEmailPrefs(patch) {
    const r = await api.put("/account/email-prefs", patch);
    return r.data.email_prefs;
}

export async function testSendWeeklyForecast() {
    // Admin-only smoke test — see backend/routes/email_prefs_routes.py.
    const r = await api.post("/account/email-prefs/test-send");
    return r.data;
}

// ---- personalized today (signed-in habit loop view) ----
export async function getTodayPersonal() {
    const r = await api.get("/today/personal");
    return r.data;
}

// ---- Live Sky — upcoming 30-day transit / dasha timeline ----
export async function getLiveSky(chartHash, { days = 30, top = 5 } = {}) {
    const r = await api.get(`/chart/${chartHash}/live-sky`, { params: { days, top } });
    return r.data;
}

// ---- PPS Lens — user-facing Pulippani rollup (tier-gated) ----
export async function getPpsLens(chartHash) {
    const r = await api.get(`/chart/${chartHash}/pps-lens`);
    return r.data;
}

export function formatApiError(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}
